import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { ReleaseNote, ReleaseNoteChange, ReleaseNoteText } from './types';

type ReleaseNotesContextValue = {
  releaseNotes: ReleaseNote[];
  latestReleaseNote?: ReleaseNote;
  isLoading: boolean;
  hasError: boolean;
};

const ReleaseNotesContext = createContext<ReleaseNotesContextValue | undefined>(undefined);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isReleaseNoteText = (value: unknown): value is ReleaseNoteText =>
  isRecord(value) && typeof value.de === 'string' && typeof value.en === 'string';

const isReleaseNoteChange = (value: unknown): value is ReleaseNoteChange =>
  isRecord(value) &&
  (value.type === 'new' || value.type === 'improved' || value.type === 'fixed') &&
  isReleaseNoteText(value.text) &&
  (value.href === undefined || typeof value.href === 'string');

const isReleaseNote = (value: unknown): value is ReleaseNote =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.version === 'string' &&
  typeof value.publishedAt === 'string' &&
  !Number.isNaN(Date.parse(value.publishedAt)) &&
  isReleaseNoteText(value.title) &&
  isReleaseNoteText(value.summary) &&
  Array.isArray(value.changes) &&
  value.changes.every(isReleaseNoteChange) &&
  (value.important === undefined || typeof value.important === 'boolean');

type ReleaseNotesProviderProps = {
  children: ReactNode;
};

export function ReleaseNotesProvider({ children }: ReleaseNotesProviderProps) {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    const loadReleaseNotes = async () => {
      try {
        const response = await fetch(`${import.meta.env.BASE_URL}release-notes.json`, {
          cache: 'no-cache',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Release notes could not be loaded (${response.status})`);
        }

        const data: unknown = await response.json();
        if (!Array.isArray(data) || !data.every(isReleaseNote)) {
          throw new Error('Release notes contain invalid data');
        }

        setReleaseNotes(
          [...data].sort((first, second) => second.publishedAt.localeCompare(first.publishedAt))
        );
        setHasError(false);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error(error);
          setHasError(true);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    loadReleaseNotes();

    return () => controller.abort();
  }, []);

  const value = useMemo(
    () => ({
      releaseNotes,
      latestReleaseNote: releaseNotes[0],
      isLoading,
      hasError,
    }),
    [hasError, isLoading, releaseNotes]
  );

  return <ReleaseNotesContext.Provider value={value}>{children}</ReleaseNotesContext.Provider>;
}

export function useReleaseNotes() {
  const context = useContext(ReleaseNotesContext);

  if (!context) {
    throw new Error('useReleaseNotes must be used inside ReleaseNotesProvider');
  }

  return context;
}
